/**
 * Plain-language formula strings shown in calculation tooltips (InfoTip).
 *
 * These mirror the single source of truth for document math in
 * `@workspace/totals` (lib/totals/src/index.ts). Generic formulas only — they
 * describe the calculation, not the row's live numbers. Keep them in sync with
 * computeDocumentTotals / computeMarginAndProfit if that math ever changes.
 */
export const FORMULAS = {
  // Document totals (estimates + invoices)
  discount:
    "Percent discount = Subtotal × rate. Fixed discount = the flat amount. Either way it's capped at the subtotal so the base never goes negative.",
  tax: "Tax = (Subtotal − Discount) × tax rate. Tax applies to the post-discount base; with multiple tax lines each rate is summed.",
  total: "Total = (Subtotal − Discount) + Tax.",
  subtotal: "Subtotal = sum of each line item's quantity × unit price.",
  profit:
    "Profit = (Subtotal − Discount) − Internal Cost. Uses the post-discount, pre-tax revenue so tax doesn't distort earnings.",
  margin:
    "Margin = Profit ÷ (Subtotal − Discount) × 100%. The share of revenue left after internal costs.",
  internalCost: "Internal Cost = sum of the material/labor costs you've recorded against this document's line items.",
  amountDue: "Amount Due = Total − payments received.",

  // Field calculators
  materialWithWaste:
    "Quantity = Area ÷ coverage per unit, then × (1 + waste %), rounded up to whole units.",
  wasteFactor: "Waste % adds extra material for cuts and offcuts — the area is multiplied by (1 + waste %).",

  // Dashboard KPIs
  dashTotalValue: "All quotes in the date range combined — every status included.",
  dashAcceptedValue: "Quotes marked Accepted or Converted to invoice.",
  dashAcceptedProfit: "Revenue from accepted quotes minus their internal costs.",
  dashPendingValue: "Quotes still in Draft or Sent — not yet decided.",
  dashAcceptanceRate:
    "Accepted ÷ (Accepted + Declined) × 100%. Excludes drafts and converted quotes.",
  dashAverageMargin:
    "Average margin across accepted quotes. Margin = profit ÷ revenue (post-discount, pre-tax).",

  // Project aggregates
  projectQuoted: "Total price of all quotes linked to this project.",
  projectInvoiced: "Total value of all invoices issued for this project.",
  projectPaid: "Sum of recorded payments across all project invoices.",
  projectOutstanding: "Amount still owed — invoiced total minus paid.",
  projectQuotedCost: "Internal costs recorded against quotes in this project.",
  projectQuotedProfit:
    "Quoted total minus quoted internal cost — projected profit at the prices quoted.",
  projectQuotedMargin: "Quoted profit ÷ quoted total × 100%.",
  projectActualCost: "Internal costs recorded against invoices — what the job has cost so far.",
  projectActualProfit: "Invoiced total minus actual cost.",
  projectActualMargin: "Actual profit ÷ invoiced total × 100%.",

  // Customer detail stats
  customerTotalQuotes: "All quotes ever created for this customer, every status.",
  customerTotalValue: "Sum of all quote totals for this customer, regardless of status.",
  customerAcceptedValue: "Sum of quotes this customer has accepted or converted to an invoice.",

  // Cost catalog stats
  catalogTimesUsed: "How many line items across all documents reference this cost item.",
  catalogTotalCost: "Unit cost × quantity summed across every line item that references this item.",

  // Invoice list columns
  invoiceReminderCol:
    "Whether an overdue reminder email has been sent for this invoice. Toggle to send one manually.",
  invoiceNextGenCol:
    "Date the next child invoice will be auto-generated for this recurring schedule.",
} as const;

export type FormulaKey = keyof typeof FORMULAS;
